/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/movatic-dev/CatalogoEcra.tsx
 * ----------------------------------------------------------------------------
 * ADR-0034 F4b-5-iii: o catalogo-ecra (MOSTRADOR) do Movatic DEV. Ecra grande,
 * superuser (ADR-0042 D3). O superuser escolhe um operador; a tela SEGUE a task
 * ativa desse operador (D7: task-assignments?operator=&is_current= + accepted_at)
 * e mostra os 3 cards tipos-mistos do passo atual (o certo + 2 distratores), com
 * codigo escaneavel. O operador pica com o COLETOR (a tela NAO tem acoes de picar
 * -- e' um mostrador). Avanca sozinha quando o next_step muda. Reusa o render do
 * CatalogViewer (CardVisual/ScanCode/buildCards -- exportados, nao duplicados).
 * ========================================================================== */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FlaskConical } from 'lucide-react'
import QRCode from 'react-qr-code'
import { useAuthStore } from '@/store/authStore'
import {
  CardVisual,
  buildCards,
  type CatEntity,
  type Catalog,
  type NextStep,
} from '@/pages/CatalogViewer'

// -------- auth (fetch()+Bearer, molde do CatalogViewer; token do superuser) --------
function buildAuthHeaders(): HeadersInit {
  const state =
    (
      useAuthStore as unknown as { getState?: () => { accessToken?: string } }
    ).getState?.() ?? {}
  const token = state.accessToken
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: buildAuthHeaders() })
  if (!r.ok) throw new Error(String(r.status))
  return (await r.json()) as T
}

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const results = (d as { results?: T[] } | null)?.results
  return Array.isArray(results) ? results : []
}

type Operator = { id: number; username?: string; full_name?: string }
type Assignment = {
  task: number
  is_current: boolean
  accepted_at: string | null
}

const opName = (o?: Operator) =>
  o ? o.full_name || o.username || `#${o.id}` : ''

// passos que mostram os 3 cards no mostrador (deliver = entregar o suporte na zona de cheios).
const SCAN_STEPS = ['support', 'location', 'pick', 'deliver']

// Ajuste 3: ao mudar de passo, segura os cards atuais este tempo antes de mostrar os novos.
const STEP_SWAP_DELAY_MS = 4000

export default function CatalogoEcra() {
  const [operatorId, setOperatorId] = useState<number | null>(null)
  const [cards, setCards] = useState<CatEntity[]>([])

  // dropdown de operadores (OperatorPagination honra page_size, max 500)
  const operatorsQ = useQuery({
    queryKey: ['mostrador-operators'],
    queryFn: async () =>
      unwrap<Operator>(await jget('/api/workforce/operators/?page_size=500')),
  })
  const operators = operatorsQ.data ?? []
  const selected = operators.find((o) => o.id === operatorId)

  // POLL barato (2.5s): task ativa (D7) + o passo atual. So' next-step; os cards
  // (catalog + buildCards) rebuild-se a' parte, so' quando o PASSO muda.
  const stepQ = useQuery({
    queryKey: ['mostrador-step', operatorId],
    enabled: operatorId != null,
    refetchInterval: 2500,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<{ ns: NextStep; taskId: number } | null> => {
      const asgs = unwrap<Assignment>(
        await jget(
          `/api/workforce/task-assignments/?operator=${operatorId}&is_current=true`,
        ),
      )
      // D7: atribuicao vigente E aceite (accepted_at != null). is_current cai no fim.
      const asg = asgs.find((a) => a.is_current && a.accepted_at)
      if (!asg) return null
      const ns = await jget<NextStep>(
        `/api/workforce/tasks/${asg.task}/next-step/`,
      )
      return { ns, taskId: asg.task }
    },
  })

  const step = stepQ.data // { ns, taskId } | null (sem task) | undefined (a carregar)
  // chave do passo: rebuild dos cards SO' quando muda (novo passo/linha) -> sem reshuffle por poll.
  const stepKey = step
    ? `${step.taskId}:${step.ns.current_step}:${step.ns.current_line?.reservation_id ?? ''}:${step.ns.expected_code ?? ''}`
    : ''

  // Ajuste 3 (delay 4s): ao MUDAR de passo (stepKey), SEGURA os cards atuais 4s antes de trocar
  // -> o operador ve' o que acabou de picar e a imagem nova nao aparece cedo (evita o scan rapido
  // picar o passo seguinte). Os novos cards pre-buscam-se ja'; o swap so' acontece aos 4s. Se um
  // novo passo chegar durante a espera, o cleanup limpa o timer pendente e reinicia (sem acumular).
  useEffect(() => {
    let cancelled = false

    const build = async (): Promise<CatEntity[]> => {
      if (!step || !SCAN_STEPS.includes(step.ns.current_step)) return []
      const { ns, taskId } = step
      // Ajuste 4 (deliver): os candidatos sao ZONAS -> trata como 'location' no buildCards
      // (o correto = a zona PICK-CHEIOS pelo expected_code; 2 distratores mistos). O catalog
      // garante a zona via include=location:<code>.
      const asZone = ns.current_step === 'deliver'
      const buildNs: NextStep = asZone
        ? { ...ns, current_step: 'location' }
        : ns
      const params = new URLSearchParams({ limit: '20' })
      if ((ns.current_step === 'location' || asZone) && ns.expected_code)
        params.set('include', `location:${ns.expected_code}`)
      else if (ns.current_step === 'pick' && ns.expected_code)
        params.set('include', `product:${ns.expected_code}`)
      try {
        const cat = await jget<Catalog>(
          `/api/workforce/tasks/${taskId}/catalog/?${params.toString()}`,
        )
        return buildCards(buildNs, cat)
      } catch {
        return []
      }
    }

    const pending = build() // pre-busca ja'; segura o swap ate' aos 4s
    const t = setTimeout(async () => {
      const next = await pending
      if (!cancelled) setCards(next)
    }, STEP_SWAP_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
    // rebuild so' quando o stepKey muda (o passo avancou no coletor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey])

  const hasTask = step != null && SCAN_STEPS.includes(step.ns.current_step)

  return (
    <div className="p-4 md:p-8 space-y-6 min-h-screen">
      {/* Topo: identidade + dropdown de operador */}
      <div className="flex flex-wrap items-center gap-3">
        <FlaskConical className="w-7 h-7 opacity-80" />
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold leading-tight">
            Catálogo — execução
          </h1>
          <div className="text-xs font-mono opacity-60">
            Movatic DEV — mostrador (o operador pica com o coletor)
          </div>
        </div>
        <select
          className="rounded-lg border px-3 py-2 bg-transparent"
          value={operatorId ?? ''}
          onChange={(e) =>
            setOperatorId(e.target.value ? Number(e.target.value) : null)
          }
          aria-label="Escolher operador"
        >
          <option value="">Escolher operador…</option>
          {operators.map((o) => (
            <option key={o.id} value={o.id}>
              {opName(o)}
            </option>
          ))}
        </select>
      </div>

      {/* Corpo */}
      {operatorId == null ? (
        <Placeholder texto="Escolhe um operador para acompanhar a execução." />
      ) : hasTask && cards.length > 0 ? (
        // 3 cards tipos-mistos, TODOS iguais (sem destacar o certo -- anti-fraude).
        // Ajuste 2: gap ~40px (bem afastados -> nao se pica o vizinho por acidente).
        <div className="grid gap-10 md:grid-cols-3 max-w-6xl mx-auto">
          {cards.map((c, i) => (
            <div
              key={`${c.kind}-${c.code}-${i}`}
              className="rounded-2xl border p-6 flex flex-col items-center gap-4 text-center"
            >
              <div className="w-full">
                <CardVisual entity={c} />
              </div>
              <div className="text-xl font-semibold leading-tight">
                {c.name || c.code}
              </div>
              <div className="font-mono text-sm opacity-60">{c.code}</div>
              <MostradorQR value={c.code} />
            </div>
          ))}
        </div>
      ) : (
        <Placeholder texto="À espera de tarefa" sub={opName(selected)} />
      )}
    </div>
  )
}

// Ajuste 1: QR UNIFORME para todos os tipos (produto/suporte/localizacao/equipamento/zona) --
// melhor leitura no ecra com brilho que o Code128. Caixa branca fixa + quiet-zone (o scanner do
// coletor le contraste real -> preto-sobre-branco, grande para o ecra grande).
function MostradorQR({ value }: { value: string }) {
  return (
    <div className="bg-white p-3 rounded-lg">
      <QRCode value={value} size={150} fgColor="#000000" bgColor="#ffffff" />
    </div>
  )
}

function Placeholder({ texto, sub }: { texto: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
      <div className="text-2xl font-medium opacity-70">{texto}</div>
      {sub && <div className="text-sm opacity-50">{sub}</div>}
    </div>
  )
}
